/**
 * Authenticated fetch wrapper for viisi-web.
 *
 * - Prefixes VITE_API_URL (defaults to "" so /api goes through Vite's dev proxy)
 * - Attaches Authorization: Bearer <idToken> on every call
 * - JSON-stringifies object bodies, parses JSON responses
 * - On 401 / 403-invalid-token: retries once after refreshing the token.
 *   If the retry also fails, signs the user out (the AuthGate re-renders to login).
 * - On 403 with a "not authorized" body (wrong user for this app):
 *   throws NotAuthorizedError so the UI can show a friendly message.
 */

import { getIdToken, logout, NotAuthorizedError } from './auth';

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');
const DEFAULT_TIMEOUT_MS = 15_000;

export class APIError extends Error {
    status: number;
    data: unknown;
    constructor(message: string, status: number, data: unknown) {
        super(message);
        this.name = 'APIError';
        this.status = status;
        this.data = data;
    }
}

export interface ApiOptions extends Omit<RequestInit, 'body'> {
    body?: unknown;
    timeoutMs?: number;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null && !(v instanceof FormData) && !(v instanceof Blob) && !(v instanceof ArrayBuffer);
}

function looksLikeInvalidTokenResponse(status: number, data: unknown): boolean {
    if (status === 401) return true;
    if (status !== 403) return false;
    if (!isPlainObject(data)) return false;
    const err = typeof data.error === 'string' ? data.error.toLowerCase() : '';
    return err.includes('invalid token') || err.includes('expired') || err.includes('unauthorized token');
}

function looksLikeNotAuthorizedForApp(status: number, data: unknown): boolean {
    if (status !== 403) return false;
    if (!isPlainObject(data)) return false;
    const err = typeof data.error === 'string' ? data.error.toLowerCase() : '';
    return err.includes('not authorized') || err.includes('forbidden');
}

async function doFetch(path: string, opts: ApiOptions, isRetry: boolean): Promise<unknown> {
    const url = path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
    const token = await getIdToken();

    const { body, timeoutMs = DEFAULT_TIMEOUT_MS, headers, ...rest } = opts;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    const finalHeaders: Record<string, string> = { ...(headers as Record<string, string> | undefined) };
    let finalBody: BodyInit | null = null;
    if (body !== undefined && body !== null) {
        if (isPlainObject(body) || Array.isArray(body)) {
            finalBody = JSON.stringify(body);
            finalHeaders['Content-Type'] ??= 'application/json';
        } else {
            finalBody = body as BodyInit;
        }
    }
    if (token) finalHeaders['Authorization'] = `Bearer ${token}`;

    let response: Response;
    try {
        response = await fetch(url, {
            ...rest,
            headers: finalHeaders,
            body: finalBody,
            signal: controller.signal,
        });
    } catch (err) {
        clearTimeout(timer);
        if ((err as Error).name === 'AbortError') {
            throw new APIError('Request timeout', 408, null);
        }
        throw new APIError('Network error', 0, null);
    }
    clearTimeout(timer);

    const text = await response.text();
    let data: unknown = null;
    if (text) {
        try { data = JSON.parse(text); } catch { data = text; }
    }

    if (!response.ok) {
        // Wrong user for this app - caller should show a friendly "not authorized" screen.
        if (looksLikeNotAuthorizedForApp(response.status, data)) {
            throw new NotAuthorizedError(
                isPlainObject(data) && typeof data.error === 'string' ? data.error : undefined
            );
        }

        // Likely-expired token: refresh and retry once.
        if (looksLikeInvalidTokenResponse(response.status, data) && !isRetry) {
            // Force a fresh token fetch - getIdToken handles the dedup.
            const fresh = await getIdToken();
            if (fresh) {
                return doFetch(path, opts, true);
            }
            // No fresh token available - sign out so AuthGate re-renders to the login form.
            logout();
            throw new APIError('Session expired', 401, data);
        }

        const message = isPlainObject(data) && typeof data.error === 'string'
            ? data.error
            : `Request failed with status ${response.status}`;
        throw new APIError(message, response.status, data);
    }

    return data;
}

/**
 * Authenticated JSON fetch. Returns the parsed response body (or null for empty bodies).
 */
export function apiFetch<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
    return doFetch(path, opts, false) as Promise<T>;
}

export { NotAuthorizedError };
